(() => {
  const productCache = new Map();

  const defaultStrings = {
    subscribe: "Subscribe",
    addToCart: window.variantStrings?.addToCart || "Add to Cart",
    soldOut: window.variantStrings?.soldOut || "Sold out",
    bundleAndSave: "Bundle & Save",
    oneTimePurchase: "One Time Purchase",
    oneTime: "One Time",
  };

  function decodeHtml(value) {
    return String(value ?? "")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }

  function getStrings() {
    const merged = Object.assign({}, defaultStrings, window.cardHoverAtcStrings || {});
    Object.keys(merged).forEach((key) => {
      merged[key] = decodeHtml(merged[key]);
    });
    return merged;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function formatMoney(cents) {
    const amount = Number(cents) / 100;
    const locale = document.documentElement.lang || undefined;
    const currency =
      window?.Shopify?.currency?.active ||
      window?.Shopify?.currency?.currentCurrency ||
      "USD";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(Number.isFinite(amount) ? amount : 0);
    } catch (_e) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(Number.isFinite(amount) ? amount : 0);
    }
  }

  function isDefaultOption(option) {
    return (
      option?.name === "Title" &&
      Array.isArray(option.values) &&
      option.values.length === 1 &&
      option.values[0] === "Default Title"
    );
  }

  function getAdjustmentPercent(plan) {
    const adjustment = plan?.price_adjustments?.[0];
    if (adjustment?.value_type === "percentage") return Number(adjustment.value) || 0;
    return 0;
  }

  function getSellingPlansForVariant(product, variant) {
    const groups = product.selling_plan_groups || [];
    const allocations = variant?.selling_plan_allocations || [];
    return allocations
      .map((allocation) => {
        let plan = null;
        for (const group of groups) {
          plan = (group.selling_plans || []).find(
            (item) => Number(item.id) === Number(allocation.selling_plan_id),
          );
          if (plan) break;
        }
        return plan
          ? {
              id: Number(plan.id),
              name: plan.name,
              price: allocation.price,
              compareAt: allocation.compare_at_price,
              percent: getAdjustmentPercent(plan),
            }
          : null;
      })
      .filter(Boolean);
  }

  function subscribeLabel(percent, compact) {
    if (compact) return getStrings().subscribe;
    if (percent > 0) return `Subscribe and save ${percent}%`;
    return "Subscribe and save";
  }

  function planLabelMarkup(full, short) {
    return `<span class="card-hover-atc__plan-label"><span class="card-hover-atc__plan-label-full">${escapeHtml(
      full,
    )}</span><span class="card-hover-atc__plan-label-short">${escapeHtml(short)}</span></span>`;
  }

  function spinnerMarkup() {
    return `<svg class="card-hover-atc__spinner spinner" aria-hidden="true" focusable="false" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
    </svg>`;
  }

  class CardHoverAtc extends HTMLElement {
    constructor() {
      super();
      this.product = null;
      this.purchaseType = "onetime";
      this.onPointerEnter = this.handlePointerEnter.bind(this);
      this.onPointerLeave = this.handlePointerLeave.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
    }

    get handle() {
      return this.getAttribute("data-handle");
    }

    get productUrl() {
      return this.getAttribute("data-url") || `/products/${this.handle}`;
    }

    get isBundle() {
      return this.getAttribute("data-is-bundle") === "true";
    }

    get hasOptionsAttr() {
      return this.getAttribute("data-has-options") === "true";
    }

    get isAvailable() {
      return this.getAttribute("data-available") !== "false";
    }

    connectedCallback() {
      this.cardWrapper = this.closest(".card-wrapper");
      if (!this.imageHost) this.imageHost = this.parentElement;
      if (this.initialized) return;
      this.initialized = true;
      if (!this.isAvailable) return;

      if (this.isBundle) {
        this.renderProductLink(getStrings().bundleAndSave);
      } else if (this.hasOptionsAttr) {
        this.renderProductLink(getStrings().addToCart);
      }

      this.cardWrapper?.addEventListener("pointerenter", this.onPointerEnter);
      this.cardWrapper?.addEventListener("pointerleave", this.onPointerLeave);
      this.addEventListener("click", (event) => {
        event.stopPropagation();
        this.handleOverlayClick(event);
      });
      this.addEventListener("pointerdown", (event) => event.stopPropagation());
      this.mediaQuery = window.matchMedia("(max-width: 989px)");
      this.onMediaChange = () => {
        if (!this.mediaQuery.matches) this.closeOverlay();
      };
      this.mediaQuery.addEventListener("change", this.onMediaChange);
    }

    disconnectedCallback() {
      if (this.relocating) return;
      this.cardWrapper?.removeEventListener("pointerenter", this.onPointerEnter);
      this.cardWrapper?.removeEventListener("pointerleave", this.onPointerLeave);
      this.mediaQuery?.removeEventListener("change", this.onMediaChange);
      if (this.abortController) this.abortController.abort();
    }

    isMobile() {
      return window.matchMedia("(max-width: 989px)").matches;
    }

    get mobileSlot() {
      return this.cardWrapper?.querySelector("[data-card-hover-slot]");
    }

    get mobileTrigger() {
      return this.cardWrapper?.querySelector("[data-card-hover-trigger]");
    }

    hasSelectableOptions(product) {
      return (product?.options || []).some((option) => !isDefaultOption(option));
    }

    shouldGoToProduct() {
      if (this.isBundle || this.hasOptionsAttr) return true;
      return this.hasSelectableOptions(this.product);
    }

    hasSubscribeOptions() {
      if (!this.product) return false;
      const variant =
        this.product.variants.find((item) => item.available) || this.product.variants[0];
      return getSellingPlansForVariant(this.product, variant).length > 1;
    }

    goToProduct() {
      window.location.href = this.productUrl;
    }

    closeOverlay() {
      this.classList.remove("is-open");
      this.classList.remove("is-plans-open");
      this.cardWrapper?.classList.remove("is-hover-atc-open");
      this.closeCustomSelects();
      this.placeOnMobile();
      this.mobileTrigger?.blur();
    }

    showPlans() {
      document.querySelectorAll("card-hover-atc.is-plans-open, card-hover-atc.is-open").forEach((el) => {
        if (el !== this) el.closeOverlay();
      });
      this.classList.add("is-plans-open");
      this.cardWrapper?.classList.add("is-hover-atc-open");
      this.placeOnMobile();
    }

    placeOnMobile() {
      const slot = this.mobileSlot;
      if (!this.imageHost) this.imageHost = this.parentElement;
      this.relocating = true;
      if (this.isMobile() && this.classList.contains("is-open") && slot) {
        if (this.parentElement !== slot) slot.appendChild(this);
      } else if (this.imageHost && this.parentElement !== this.imageHost) {
        this.imageHost.appendChild(this);
      }
      this.relocating = false;
    }

    async handleMobileTrigger(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.triggerLoading) return;
      if (this.shouldGoToProduct()) {
        this.goToProduct();
        return;
      }
      if (this.classList.contains("is-open") || this.classList.contains("is-plans-open")) {
        await this.handleSubmit(event);
        return;
      }
      this.triggerLoading = true;
      try {
        if (!this.product) {
          await this.loadProduct();
        }
        if (this.shouldGoToProduct()) {
          this.goToProduct();
          return;
        }
        if (!this.hasSubscribeOptions()) {
          await this.handleSubmit(event);
          return;
        }
        document.querySelectorAll("card-hover-atc.is-open").forEach((el) => {
          if (el !== this) el.closeOverlay();
        });
        this.classList.add("is-open");
        this.showPlans();
        this.syncState();
      } finally {
        this.triggerLoading = false;
      }
    }

    handlePointerLeave() {
      if (this.isMobile()) return;
      this.classList.remove("is-plans-open");
      this.closeCustomSelects();
    }

    handlePointerEnter() {
      if (this.isMobile()) return;
      if (!this.isAvailable) return;
      if (this.isBundle || this.hasOptionsAttr) return;
      if (this.product || this.loading) return;
      this.loadProduct();
    }

    async loadProduct() {
      this.loading = true;
      try {
        if (!productCache.has(this.handle)) {
          this.abortController = new AbortController();
          const response = await fetch(`/products/${this.handle}.js`, {
            signal: this.abortController.signal,
          });
          if (!response.ok) throw new Error("Unable to load product");
          productCache.set(this.handle, await response.json());
        }
        this.product = productCache.get(this.handle);
        if (this.shouldGoToProduct()) {
          this.renderProductLink(getStrings().addToCart);
          return;
        }
        this.renderProduct();
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error(error);
        }
      } finally {
        this.loading = false;
      }
    }

    renderProductLink(label) {
      const strings = getStrings();
      this.innerHTML = `<a class="card-hover-atc__submit" href="${escapeHtml(
        this.productUrl,
      )}"></a>`;
      const link = this.querySelector("a");
      if (link) link.textContent = label || strings.addToCart;
      this.setAttribute("data-ready", "");
    }

    renderProduct() {
      if (!this.product) return;
      const strings = getStrings();

      const variant =
        this.product.variants.find((item) => item.available) || this.product.variants[0];
      const sellingPlans = getSellingPlansForVariant(this.product, variant);
      const subscriptionOnly = Boolean(this.product.requires_selling_plan);
      const showWidget = sellingPlans.length > 1;
      const showOnetime = showWidget && !subscriptionOnly;
      this.purchaseType = showWidget ? "subscription" : "onetime";

      const caretMarkup = `<svg class="card-hover-atc__select-caret" aria-hidden="true" focusable="false" viewBox="0 0 10 6">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
            </svg>`;

      const customSelectInner = ({ name, selectedValue, values, inputAttrs, triggerLabel }) => {
        const selected =
          values.find((item) => String(item.value) === String(selectedValue)) || values[0];
        const options = values
          .map(
            (item) =>
              `<li role="none"><button type="button" class="card-hover-atc__select-option" role="option" data-value="${escapeHtml(
                String(item.value),
              )}" aria-selected="${String(item.value) === String(selected?.value)}">${escapeHtml(
                item.label,
              )}</button></li>`,
          )
          .join("");
        const nameMarkup = name
          ? `<span class="card-hover-atc__select-name">${escapeHtml(name)}:</span>`
          : "";
        return `<button type="button" class="card-hover-atc__select-trigger" aria-expanded="false" aria-haspopup="listbox" aria-label="${escapeHtml(
          triggerLabel || name || selected?.label || "",
        )}">
            <span class="card-hover-atc__select-display">
              ${nameMarkup}
              <span class="card-hover-atc__select-value">${escapeHtml(selected?.label || "")}</span>
            </span>
            ${caretMarkup}
          </button>
          <ul class="card-hover-atc__select-menu" role="listbox">${options}</ul>
          <input type="hidden" ${inputAttrs} value="${escapeHtml(String(selected?.value || ""))}">`;
      };

      const uid = this.product.id;
      const firstPlan = sellingPlans[0];
      const subscribePrice = firstPlan ? firstPlan.price : variant.price;
      const frequencyValues = sellingPlans.map((plan) => ({
        value: String(plan.id),
        label: plan.name,
      }));
      const subscribeChecked = this.purchaseType === "subscription";

      const widgetMarkup = showWidget
        ? `<div class="card-hover-atc__plan card-hover-atc__card" data-plan-type="subscription">
            <label class="card-hover-atc__plan-row" for="card-hover-sub-${uid}">
              <input id="card-hover-sub-${uid}" type="radio" name="card-hover-plan-${uid}" value="subscription"${
                subscribeChecked ? " checked" : ""
              }>
              <span class="card-hover-atc__check"></span>
              <span class="card-hover-atc__plan-copy">
                ${planLabelMarkup(
                  subscribeLabel(firstPlan?.percent, false),
                  subscribeLabel(firstPlan?.percent, true),
                )}
                <span class="card-hover-atc__plan-price" data-subscribe-price>${formatMoney(
                  subscribePrice,
                )}</span>
              </span>
            </label>
            <div class="card-hover-atc__frequency${
              this.purchaseType === "subscription" ? "" : " is-hidden"
            }" data-custom-select>
              ${customSelectInner({
                selectedValue: firstPlan ? String(firstPlan.id) : "",
                values: frequencyValues,
                inputAttrs: `data-selling-plan`,
                triggerLabel: "Delivery frequency",
              })}
            </div>
          </div>
          ${
            showOnetime
              ? `<div class="card-hover-atc__plan card-hover-atc__card" data-plan-type="onetime">
            <label class="card-hover-atc__plan-row" for="card-hover-otp-${uid}">
              <input id="card-hover-otp-${uid}" type="radio" name="card-hover-plan-${uid}" value="onetime"${
                this.purchaseType === "onetime" ? " checked" : ""
              }>
              <span class="card-hover-atc__check"></span>
              <span class="card-hover-atc__plan-copy">
                ${planLabelMarkup(strings.oneTimePurchase, strings.oneTime)}
                <span class="card-hover-atc__plan-price" data-onetime-price>${formatMoney(
                  variant.price,
                )}</span>
              </span>
            </label>
          </div>`
              : ""
          }`
        : "";

      const buttonLabel = this.getButtonLabel(variant);

      this.innerHTML = `<div class="card-hover-atc__plans" data-hover-plans>${widgetMarkup}</div>
        <button type="button" class="card-hover-atc__submit" data-hover-submit>
          <span class="card-hover-atc__submit-label">${escapeHtml(buttonLabel)}</span>
          ${spinnerMarkup()}
        </button>`;

      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener("change", this.onChange);
      });
      this.querySelector("[data-hover-submit]")?.addEventListener("click", this.onSubmit);
      this.syncState();
      this.setAttribute("data-ready", "");
    }

    getSelectedOptions() {
      return [...this.querySelectorAll("[data-option-position]")].map((select) => ({
        position: Number(select.dataset.optionPosition),
        value: select.value,
      }));
    }

    getSelectedVariant() {
      const selected = this.getSelectedOptions();
      return (
        this.product.variants.find((variant) =>
          selected.every(({ position, value }) => variant[`option${position}`] === value),
        ) ||
        this.product.variants.find((variant) => variant.available) ||
        this.product.variants[0]
      );
    }

    getButtonLabel(variant) {
      const strings = getStrings();
      if (!variant?.available) return strings.soldOut;
      return strings.addToCart;
    }

    handleChange() {
      const variant = this.getSelectedVariant();
      const sellingPlans = getSellingPlansForVariant(this.product, variant);
      const selectedPlanType = this.querySelector('input[name^="card-hover-plan-"]:checked')
        ?.value;
      if (selectedPlanType) this.purchaseType = selectedPlanType;

      const subscribePriceEl = this.querySelector("[data-subscribe-price]");
      const onetimePriceEl = this.querySelector("[data-onetime-price]");
      const frequencyInput = this.querySelector("[data-selling-plan]");
      const frequencyWrap = frequencyInput?.closest(".card-hover-atc__frequency");

      if (onetimePriceEl) {
        onetimePriceEl.textContent = formatMoney(variant.price);
      }

      this.rebuildFrequencyMenu(sellingPlans, frequencyInput?.value);

      if (subscribePriceEl) {
        const selectedPlanId = frequencyInput?.value;
        const selectedPlan =
          sellingPlans.find((plan) => String(plan.id) === String(selectedPlanId)) ||
          sellingPlans[0];
        const subscribePrice = selectedPlan ? selectedPlan.price : variant.price;
        subscribePriceEl.textContent = formatMoney(subscribePrice);
        const labelRoot = this.querySelector(
          '[data-plan-type="subscription"] .card-hover-atc__plan-label',
        );
        if (labelRoot) {
          const fullEl = labelRoot.querySelector(".card-hover-atc__plan-label-full");
          const shortEl = labelRoot.querySelector(".card-hover-atc__plan-label-short");
          if (fullEl) fullEl.textContent = subscribeLabel(selectedPlan?.percent, false);
          if (shortEl) shortEl.textContent = subscribeLabel(selectedPlan?.percent, true);
        }
      }

      if (frequencyWrap) {
        frequencyWrap.classList.toggle("is-hidden", this.purchaseType !== "subscription");
      }

      this.syncSelectDisplays();
      this.syncState();
    }

    syncSelectDisplays() {
      this.querySelectorAll("[data-custom-select]").forEach((root) => {
        const input = root.querySelector("[data-option-position], [data-selling-plan]");
        const valueEl = root.querySelector(".card-hover-atc__select-value");
        if (!input || !valueEl) return;
        const selected = root.querySelector(
          `.card-hover-atc__select-option[data-value="${CSS.escape(String(input.value))}"]`,
        );
        if (selected) valueEl.textContent = selected.textContent.trim();
      });
    }

    closeCustomSelects() {
      this.querySelectorAll("[data-custom-select].is-open").forEach((root) => {
        root.classList.remove("is-open");
        root.querySelector(".card-hover-atc__select-trigger")?.setAttribute("aria-expanded", "false");
      });
    }

    chooseCustomOption(root, value, label) {
      if (!root) return;
      const input = root.querySelector("[data-option-position], [data-selling-plan]");
      if (input) input.value = value;
      const valueEl = root.querySelector(".card-hover-atc__select-value");
      if (valueEl) valueEl.textContent = String(label || "").trim();
      root.querySelectorAll("[role='option']").forEach((opt) => {
        opt.setAttribute(
          "aria-selected",
          String(opt.getAttribute("data-value")) === String(value) ? "true" : "false",
        );
      });
      this.closeCustomSelects();
      this.handleChange();
    }

    rebuildFrequencyMenu(sellingPlans, currentValue) {
      const root = this.querySelector(".card-hover-atc__frequency[data-custom-select]");
      if (!root || !sellingPlans.length) return;
      const selected =
        sellingPlans.find((plan) => String(plan.id) === String(currentValue)) || sellingPlans[0];
      const input = root.querySelector("[data-selling-plan]");
      if (input) input.value = selected.id;
      const valueEl = root.querySelector(".card-hover-atc__select-value");
      if (valueEl) valueEl.textContent = selected.name;
      const menu = root.querySelector(".card-hover-atc__select-menu");
      if (!menu) return;
      menu.innerHTML = sellingPlans
        .map(
          (plan) =>
            `<li role="none"><button type="button" class="card-hover-atc__select-option" role="option" data-value="${escapeHtml(
              String(plan.id),
            )}" aria-selected="${String(plan.id) === String(selected.id)}">${escapeHtml(
              plan.name,
            )}</button></li>`,
        )
        .join("");
    }

    handleOverlayClick(event) {
      const optionBtn = event.target.closest(".card-hover-atc__select-option");
      if (optionBtn) {
        event.preventDefault();
        const root = optionBtn.closest("[data-custom-select]");
        this.chooseCustomOption(root, optionBtn.dataset.value, optionBtn.textContent);
        return;
      }
      const trigger = event.target.closest(".card-hover-atc__select-trigger");
      if (trigger) {
        event.preventDefault();
        const root = trigger.closest("[data-custom-select]");
        const willOpen = !root.classList.contains("is-open");
        this.closeCustomSelects();
        if (willOpen && root) {
          root.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
        }
        return;
      }
      this.closeCustomSelects();
    }

    getActionButton() {
      if (this.isMobile()) return this.mobileTrigger;
      return this.querySelector("[data-hover-submit]");
    }

    syncState() {
      const variant = this.getSelectedVariant();
      const buttons = [this.querySelector("[data-hover-submit]"), this.mobileTrigger].filter(
        Boolean,
      );
      const available = Boolean(variant?.available);
      const text = this.getButtonLabel(variant);
      buttons.forEach((button) => {
        button.disabled = !available;
        const label = button.querySelector(".card-hover-atc__submit-label");
        if (label) label.textContent = text;
      });
    }

    getCart() {
      return document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      const button = this.getActionButton();
      if (!button || button.disabled || button.classList.contains("loading")) return;

      if (this.hasSubscribeOptions() && !this.classList.contains("is-plans-open")) {
        this.showPlans();
        return;
      }

      const variant = this.getSelectedVariant();
      if (!variant?.available) return;

      const sellingPlans = getSellingPlansForVariant(this.product, variant);
      const sellingPlanInput = this.querySelector("[data-selling-plan]");
      const useSellingPlan =
        this.purchaseType === "subscription" && sellingPlans.length > 0;
      const sellingPlanId = useSellingPlan
        ? sellingPlanInput?.value || sellingPlans[0].id
        : null;

      const cart = this.getCart();
      const formData = new FormData();
      formData.append("id", String(variant.id));
      formData.append("quantity", "1");
      if (sellingPlanId) formData.append("selling_plan", String(sellingPlanId));
      if (cart?.getSectionsToRender) {
        formData.append(
          "sections",
          cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(","),
        );
        formData.append("sections_url", window.location.pathname);
        if (typeof cart.setActiveElement === "function") {
          cart.setActiveElement(document.activeElement);
        }
      }

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = formData;

      button.classList.add("loading");
      button.setAttribute("aria-disabled", "true");

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        const parsed = await response.json();
        if (parsed.status) {
          console.error(parsed.description);
          return;
        }
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }
        if (typeof publish === "function") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "product-form" });
        }
        cart.renderContents(parsed, true);
        if (this.isMobile()) this.closeOverlay();
      } catch (error) {
        console.error(error);
      } finally {
        button.classList.remove("loading");
        if (cart?.classList.contains("is-empty")) cart.classList.remove("is-empty");
        button.removeAttribute("aria-disabled");
        document.dispatchEvent(new CustomEvent("Cart:Drawer:Changed"));
      }
    }
  }

  if (!customElements.get("card-hover-atc")) {
    customElements.define("card-hover-atc", CardHoverAtc);
  }

  function closeOpenCustomSelects() {
    document.querySelectorAll("[data-custom-select].is-open").forEach((root) => {
      root.classList.remove("is-open");
      root.querySelector(".card-hover-atc__select-trigger")?.setAttribute("aria-expanded", "false");
    });
  }

  function closeOpenHoverOverlays() {
    document.querySelectorAll("card-hover-atc.is-open, card-hover-atc.is-plans-open").forEach((el) => {
      el.closeOverlay();
    });
  }

  document.addEventListener(
    "scroll",
    (event) => {
      if (!document.querySelector("card-hover-atc.is-open, card-hover-atc.is-plans-open")) return;
      if (event.target instanceof Element && event.target.closest("card-hover-atc")) return;
      closeOpenHoverOverlays();
    },
    { capture: true, passive: true },
  );

  document.addEventListener(
    "click",
    (event) => {
      if (!event.target.closest("[data-custom-select]")) {
        closeOpenCustomSelects();
      }

      const trigger = event.target.closest("[data-card-hover-trigger]");
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        const wrapper = trigger.closest(".card-wrapper");
        const hoverAtc = wrapper?.querySelector("card-hover-atc");
        if (hoverAtc && typeof hoverAtc.handleMobileTrigger === "function") {
          hoverAtc.handleMobileTrigger(event);
          return;
        }
        const href =
          trigger.getAttribute("data-url") ||
          wrapper?.querySelector("a.full-unstyled-link")?.getAttribute("href");
        if (href) window.location.href = href;
        return;
      }

      if (
        !event.target.closest("card-hover-atc") &&
        !event.target.closest("[data-card-hover-slot]")
      ) {
        closeOpenHoverOverlays();
      }
    },
    true,
  );
})();
